/**
 * DataWorkflow Component
 * ======================
 * Handles the data processing workflow for FIS or USSA:
 * Clean Data → Calculate Penalty / Race Points → Download XML
 * 
 * All racer data uses CANONICAL field names only (normalized at import).
 * See columnNormalizer.js CANONICAL_FIELDS for the definitive list.
 * 
 * Matches the original CalculatePoints modal functionality:
 * - Calculate Penalty button (from top 5 racers, middle 3)
 * - Editable penalty input (can be overridden)
 * - F-Factor selection with descriptive labels
 * - Separate "Assign Race Points" button
 * - Top 5 racers table with highlighting
 */

import React, { useState, useEffect } from 'react';
import { CANONICAL_FIELDS, dataHasField } from '../utils/columnNormalizer';
import './DataWorkflow.css';

// F-Factor options matching the old implementation
const F_FACTOR_OPTIONS = [
  { value: 0, label: 'No Points (Sprint Final)' },
  { value: 800, label: '800 - Interval Start' },
  { value: 1200, label: '1200 - Sprint/Pursuit' },
  { value: 1400, label: '1400 - Mass Start/Skiathlon' },
  { value: 2800, label: '2800 - FIS Long Distance Popular' },
];

// Display-friendly labels for canonical field names
const DISPLAY_LABELS = {
  [CANONICAL_FIELDS.RANK]: 'Rank',
  [CANONICAL_FIELDS.STATUS]: 'Status',
  [CANONICAL_FIELDS.BIB]: 'Bib',
  [CANONICAL_FIELDS.FIRST_NAME]: 'First Name',
  [CANONICAL_FIELDS.LAST_NAME]: 'Last Name',
  [CANONICAL_FIELDS.FIS_CODE]: 'FIS ID',
  [CANONICAL_FIELDS.USSA_CODE]: 'USSA ID',
  [CANONICAL_FIELDS.FIS_POINTS]: 'FIS Pts',
  [CANONICAL_FIELDS.USSA_POINTS]: 'NRL Pts',
  [CANONICAL_FIELDS.COUNTRY]: 'Nation',
  [CANONICAL_FIELDS.YOB]: 'YOB',
  [CANONICAL_FIELDS.CLUB]: 'Club',
  [CANONICAL_FIELDS.TIME]: 'Time',
  [CANONICAL_FIELDS.RACE_POINTS]: 'Race Pts',
  [CANONICAL_FIELDS.HIGHEST_LEVEL]: 'Level',
};

// Top 5 table columns (canonical field names)
const TOP5_FIELDS = [
  CANONICAL_FIELDS.RANK, CANONICAL_FIELDS.TIME,
  CANONICAL_FIELDS.FIRST_NAME, CANONICAL_FIELDS.LAST_NAME,
  CANONICAL_FIELDS.FIS_CODE, CANONICAL_FIELDS.USSA_CODE,
  CANONICAL_FIELDS.FIS_POINTS, CANONICAL_FIELDS.USSA_POINTS,
  CANONICAL_FIELDS.COUNTRY, CANONICAL_FIELDS.RACE_POINTS,
];

function DataWorkflow({ 
  type, // 'fis' or 'ussa'
  racers,
  setRacers,
  headerInfo,
  jury,
  workflowState,
  setWorkflowState,
  onClean,
  onCalculatePoints,
  onDownloadXML,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(null);
  const [fFactor, setFFactor] = useState(800);
  const [calculatedPenalty, setCalculatedPenalty] = useState('');
  const [appliedPenalty, setAppliedPenalty] = useState('');
  const [top5Racers, setTop5Racers] = useState([]);
  const [showPointsModal, setShowPointsModal] = useState(false);
  
  const isFIS = type === 'fis';
  const label = isFIS ? 'FIS' : 'USSA';
  
  // Check if race has both FIS and USSA codexes (combined race)
  const hasFisCodex = !!headerInfo?.fisCodex;
  const hasUssaCode = !!headerInfo?.ussaCode;
  const isCombinedRace = hasFisCodex && hasUssaCode;
  
  // Check if any racers have FIS data (may have been fetched during cleanup)
  // Data is normalized — just check canonical field names directly
  const hasFisCodeData = dataHasField(racers, CANONICAL_FIELDS.FIS_CODE);
  const hasFisPointsData = dataHasField(racers, CANONICAL_FIELDS.FIS_POINTS);
  
  // Check if this is a sprint heats format
  const hasSprintHeatsData = dataHasField(racers, CANONICAL_FIELDS.HIGHEST_LEVEL);
  
  // Determine workflow state
  const isCleanComplete = workflowState?.cleaned || false;
  const isPointsComplete = workflowState?.pointsCalculated || false;
  const isExported = workflowState?.exported || false;
  
  // Sprint Finals don't require points calculation - can download after cleaning
  // Check eventType (from API), format (from header dropdown), or fFactor=0
  const isSprintFinal = headerInfo?.eventType === 'Final' || 
                        headerInfo?.format === 'sprintFinal' ||
                        fFactor === 0;
  const canDownload = isPointsComplete || (isSprintFinal && isCleanComplete);

  // Calculate top 5 racers when modal opens or racers change
  useEffect(() => {
    if (showPointsModal && racers && racers.length > 0) {
      calculateTop5Racers();
    }
  }, [showPointsModal, racers]);
  
  // Auto-set F-Factor based on event type or format
  useEffect(() => {
    let autoFactor = 800; // Default
    
    // Check format field first (for FIS-only races where eventType may not be set)
    // Valid format values: individualDistance, sprintQualification, sprintFinal, team, teamSprint
    if (headerInfo?.format) {
      switch (headerInfo.format) {
        case 'sprintFinal':
          autoFactor = 0; // No points for Sprint Final
          break;
        case 'sprintQualification':
          autoFactor = 1200;
          break;
        default:
          // individualDistance and others use default 800
          break;
      }
    }
    
    // Check eventType (from USSA API) - overrides format if set
    if (headerInfo?.eventType) {
      switch (headerInfo.eventType) {
        case 'IS': // Interval Start
          autoFactor = 800;
          break;
        case 'MS': // Mass Start
        case 'Ski': // Skiathlon
          autoFactor = 1400;
          break;
        case 'Qual': // Sprint Qualification
        case 'Pur': // Pursuit
          autoFactor = 1200;
          break;
        case 'Final': // Sprint Final - no points calculated
          autoFactor = 0; // Will disable calculation
          break;
        default:
          // Keep the autoFactor from format check or default
          break;
      }
    }
    
    setFFactor(autoFactor);
  }, [headerInfo?.eventType, headerInfo?.format]);

  // Calculate top 5 racers (those with rank 1-5)
  const calculateTop5Racers = () => {
    const filteredRacers = racers.filter(racer => {
      const rank = parseInt(racer.rank || 0);
      return rank >= 1 && rank <= 5;
    });
    
    const sortedByRank = [...filteredRacers].sort(
      (a, b) => (parseInt(a.rank) || 0) - (parseInt(b.rank) || 0)
    );
    
    setTop5Racers(sortedByRank);
  };

  // Get penalty-relevant points for a racer
  // USSA: prefer NRL points, fall back to FIS. FIS: use FIS points.
  const getPenaltyPoints = (racer) => {
    if (type === 'ussa') {
      const nrl = parseFloat(racer.ussaPoints || 0);
      if (nrl && nrl !== 990) return nrl;
      return parseFloat(racer.fisPoints || 0);
    }
    return parseFloat(racer.fisPoints || 0);
  };

  // Calculate penalty from top 5 racers (middle 3 by points)
  const handleCalculatePenalty = () => {
    if (top5Racers.length < 5) {
      alert('Need at least 5 ranked racers to calculate penalty');
      return;
    }

    // Debug: Log raw racer data to see what fields exist
    console.log('[Penalty Calc] Top 5 racers raw:', top5Racers);
    
    // Sort by points to find middle 3
    const sortedByPoints = [...top5Racers].sort((a, b) => getPenaltyPoints(a) - getPenaltyPoints(b));
    
    // Debug: Log points for each racer
    sortedByPoints.forEach((r, i) => {
      console.log(`[Penalty Calc] Racer ${i}: ${r.firstName} ${r.lastName} - points: ${getPenaltyPoints(r)}`);
    });
    
    // Middle 3 are indices 1, 2, 3 (skip best and worst)
    const racer1Points = getPenaltyPoints(sortedByPoints[1]) || 0;
    const racer2Points = getPenaltyPoints(sortedByPoints[2]) || 0;
    const racer3Points = getPenaltyPoints(sortedByPoints[3]) || 0;

    console.log('[Penalty Calc] Middle 3 points:', racer1Points, racer2Points, racer3Points);

    const mid3Total = racer1Points + racer2Points + racer3Points;
    const penaltyValue = mid3Total / 3.5;
    
    console.log('[Penalty Calc] Total:', mid3Total, 'Penalty:', penaltyValue);
    
    // Round to 2 decimal places
    const roundedPenalty = (Math.floor(penaltyValue * 100 + 0.5) / 100).toFixed(2);
    
    // Set both calculated and applied (applied can be overridden)
    setCalculatedPenalty(roundedPenalty);
    setAppliedPenalty(roundedPenalty);
  };

  // Convert time string to seconds
  const convertTimeToSeconds = (time) => {
    if (!time) return 0;
    const timeParts = time.toString().split(':');
    
    let hours = 0, minutes = 0, seconds = 0;
    
    if (timeParts.length === 3) {
      hours = parseFloat(timeParts[0]) || 0;
      minutes = parseFloat(timeParts[1]) || 0;
      seconds = parseFloat(timeParts[2]) || 0;
    } else if (timeParts.length === 2) {
      minutes = parseFloat(timeParts[0]) || 0;
      seconds = parseFloat(timeParts[1]) || 0;
    }
    
    return (hours * 3600) + (minutes * 60) + seconds;
  };

  // Assign race points to all racers
  const handleAssignRacePoints = () => {
    if (!fFactor || fFactor === 0) {
      alert('Please select an F-Factor');
      return;
    }

    // Find winner's time (rank 1)
    const winner = racers.find(r => parseInt(r.rank) === 1);
    if (!winner) {
      alert('Cannot find winner (rank 1) to calculate race points');
      return;
    }

    const To = convertTimeToSeconds(winner.time);
    if (To === 0) {
      alert('Winner has no valid time');
      return;
    }

    // Calculate race points for each racer
    const newRacers = racers.map(racer => {
      const status = racer.status || '';
      const time = racer.time || '';
      
      if (status === '' && time !== '') {
        const Tx = convertTimeToSeconds(time);
        const racePoints = ((Tx / To) - 1.0) * fFactor;
        const roundedPoints = (Math.floor(racePoints * 100 + 0.5) / 100).toFixed(2);
        
        return {
          ...racer,
          racePoints: roundedPoints,
        };
      }
      return racer;
    });

    setRacers(newRacers);
    setWorkflowState(prev => ({ 
      ...prev, 
      pointsCalculated: true,
      calculatedPenalty: calculatedPenalty,
      penalty: appliedPenalty, // The applied penalty is what gets used
      fFactor: fFactor,
    }));
    
    // Update top5 with new race points
    calculateTop5Racers();
  };

  // Get sorted racers by points for highlighting
  const getSortedByPoints = () => {
    return [...top5Racers].sort((a, b) => getPenaltyPoints(a) - getPenaltyPoints(b));
  };

  // Check if racer is one of the middle 3 (used in penalty calc)
  const isMiddleRacer = (racer) => {
    const sorted = getSortedByPoints();
    const idx = sorted.findIndex(r => r === racer);
    return idx >= 1 && idx <= 3;
  };

  // Handle Clean Data
  const handleClean = async () => {
    setIsProcessing(true);
    setProcessingStep('clean');
    try {
      await onClean(type);
      setWorkflowState(prev => ({ ...prev, cleaned: true }));
    } catch (err) {
      console.error('Clean error:', err);
      alert(`Error cleaning ${label} data: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingStep(null);
    }
  };

  // Handle Download XML
  const handleDownload = async () => {
    setIsProcessing(true);
    setProcessingStep('download');
    try {
      await onDownloadXML(type);
      setWorkflowState(prev => ({ ...prev, exported: true }));
    } catch (err) {
      console.error('Download error:', err);
      alert(`Error generating XML: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingStep(null);
    }
  };

  const getStepStatus = (step) => {
    if (processingStep === step) return 'processing';
    switch (step) {
      case 'clean': return isCleanComplete ? 'complete' : 'ready';
      case 'points': 
        // Sprint Finals skip points calculation
        if (isSprintFinal) return isCleanComplete ? 'skipped' : 'pending';
        return isPointsComplete ? 'complete' : (isCleanComplete ? 'ready' : 'pending');
      case 'download': return isExported ? 'complete' : (canDownload ? 'ready' : 'pending');
      default: return 'pending';
    }
  };

  return (
    <div className={`data-workflow ${type}`}>
      {/* Workflow Steps Header */}
      <div className="workflow-steps">
        {/* Step 1: Clean Data */}
        <div className={`workflow-step ${getStepStatus('clean')}`}>
          <div className="step-number">1</div>
          <div className="step-content">
            <div className="step-title">Clean {label} Data</div>
            <button
              className="step-btn"
              onClick={handleClean}
              disabled={isProcessing}
            >
              {processingStep === 'clean' ? '⏳ Cleaning...' : isCleanComplete ? '✓ Re-Clean' : '🔄 Clean Data'}
            </button>
          </div>
        </div>

        <div className="workflow-arrow">→</div>

        {/* Step 2: Calculate Penalty / Race Points */}
        <div className={`workflow-step ${getStepStatus('points')}`}>
          <div className="step-number">2</div>
          <div className="step-content">
            <div className="step-title">Penalty / Race Points</div>
            <button
              className="step-btn"
              onClick={() => setShowPointsModal(true)}
              disabled={isProcessing}
            >
              {isPointsComplete ? '✓ Edit Points' : '📊 Calculate'}
            </button>
            {appliedPenalty && (
              <div className="penalty-display">
                Penalty: <strong>{appliedPenalty}</strong>
                {calculatedPenalty && calculatedPenalty !== appliedPenalty && (
                  <span className="calculated-note"> (calc: {calculatedPenalty})</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="workflow-arrow">→</div>

        {/* Step 3: Download XML */}
        <div className={`workflow-step ${getStepStatus('download')}`}>
          <div className="step-number">3</div>
          <div className="step-content">
            <div className="step-title">Download XML</div>
            <button
              className="step-btn download-btn"
              onClick={handleDownload}
              disabled={isProcessing || !canDownload}
            >
              {processingStep === 'download' ? '⏳ Generating...' : isExported ? '✓ Re-Download' : '⬇️ Download'}
            </button>
          </div>
        </div>
      </div>

      {/* Race Points Modal (matches old CalculatePoints component) */}
      {showPointsModal && (
        <div className="points-modal-overlay" onClick={() => setShowPointsModal(false)}>
          <div className="points-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Race Points</h2>
            
            <div className="points-modal-content">
              {/* Calculate Penalty Section */}
              <div className="calc-penalty-section">
                <button 
                  className="calc-penalty-btn"
                  onClick={handleCalculatePenalty}
                  disabled={top5Racers.length < 5}
                >
                  Calculate Penalty
                </button>
              </div>
              
              {/* Calculated Penalty (read-only display) */}
              <div className="penalty-input-row">
                <label>Calculated Penalty:</label>
                <input
                  type="text"
                  value={calculatedPenalty || '—'}
                  readOnly
                  className="readonly-input"
                />
              </div>
              
              {/* Applied Penalty (editable for override) */}
              <div className="penalty-input-row">
                <label htmlFor="appliedPenaltyInput">Applied Penalty:</label>
                <input
                  id="appliedPenaltyInput"
                  type="number"
                  step="0.01"
                  value={appliedPenalty}
                  onChange={(e) => setAppliedPenalty(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              
              {/* USSA Minimum Penalty Warning */}
              {!isFIS && calculatedPenalty && parseFloat(calculatedPenalty) < 35 && (
                <div className="penalty-warning">
                  <span className="warning-icon">⚠️</span>
                  <div className="warning-text">
                    <strong>USSA Minimum Penalty Warning</strong>
                    <p>Calculated penalty ({calculatedPenalty}) is below USSA minimums:</p>
                    <ul>
                      <li><strong>35</strong> — Non-Championship races (divisional, college, junior)</li>
                      <li><strong>100</strong> — Competitions restricted to U16 and younger</li>
                    </ul>
                    <p>If applicable, enter the minimum penalty in the Applied Penalty field above.</p>
                  </div>
                </div>
              )}
              
              {/* F-Factor Selection */}
              <div className="ffactor-input-row">
                <label htmlFor="ffactorSelect">F-Factor:</label>
                <select
                  id="ffactorSelect"
                  value={fFactor}
                  onChange={(e) => setFFactor(Number(e.target.value))}
                >
                  {F_FACTOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Sprint Final Notice */}
              {fFactor === 0 && (
                <div className="final-notice">
                  <span className="notice-icon">ℹ️</span>
                  <span>Sprint Finals do not have race points calculated. You can proceed to download.</span>
                </div>
              )}
              
              {/* Assign Race Points Button */}
              <div className="assign-points-section">
                <button 
                  className="assign-points-btn"
                  onClick={handleAssignRacePoints}
                  disabled={racers.length === 0 || fFactor === 0}
                >
                  Assign Race Points
                </button>
              </div>
            </div>
            
            {/* Top 5 Racers Table */}
            <div className="top5-section">
              <h3>Top 5 Racers</h3>
              <div className="top5-table-wrapper">
                <table className="top5-table">
                  <thead>
                    <tr>
                      {TOP5_FIELDS.map((field, idx) => (
                        <th key={idx}>{DISPLAY_LABELS[field] || field}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {top5Racers.map((racer, idx) => {
                      const isMid = isMiddleRacer(racer);
                      const nrlValue = parseFloat(racer.ussaPoints || 0);
                      const hasValidNRL = nrlValue && nrlValue !== 990;
                      return (
                        <tr key={idx}>
                          {TOP5_FIELDS.map((field, idx2) => {
                            // Highlight the points column for middle racers
                            const isHighlighted = isMid && (
                              (field === CANONICAL_FIELDS.USSA_POINTS && type === 'ussa' && hasValidNRL) ||
                              (field === CANONICAL_FIELDS.FIS_POINTS && (type === 'fis' || !hasValidNRL))
                            );
                            return (
                              <td key={idx2} className={isHighlighted ? 'highlight' : ''}>
                                {racer[field] ?? ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {top5Racers.length < 5 && (
                <p className="top5-warning">⚠️ Need at least 5 ranked racers to calculate penalty</p>
              )}
            </div>
            
            {/* Close Button */}
            <div className="modal-buttons">
              <button className="close-btn" onClick={() => setShowPointsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="data-preview">
        {racers && racers.length > 0 ? (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Status</th>
                  <th>Bib</th>
                  <th>Name</th>
                  <th>Nation</th>
                  <th>YOB</th>
                  <th>Club</th>
                  {/* For FIS tab: show FIS ID */}
                  {isFIS && <th>FIS ID</th>}
                  {/* For USSA tab: show USSA ID, and FIS ID if data present */}
                  {!isFIS && <th>USSA ID</th>}
                  {!isFIS && (isCombinedRace || hasFisCodeData) && <th>FIS ID</th>}
                  {/* For FIS tab: show FIS Pts */}
                  {isFIS && <th>FIS Pts</th>}
                  {/* For USSA tab: show NRL Pts, and FIS Pts if data present */}
                  {!isFIS && <th>NRL Pts</th>}
                  {!isFIS && (isCombinedRace || hasFisPointsData) && <th>FIS Pts</th>}
                  <th>Time</th>
                  {/* Sprint heats: show Highest Level column */}
                  {hasSprintHeatsData && <th>Level</th>}
                  {isPointsComplete && <th>Race Pts</th>}
                </tr>
              </thead>
              <tbody>
                {racers.map((racer, idx) => (
                  <tr key={idx} className={racer._modified ? 'modified' : ''}>
                    <td>{racer.rank || idx + 1}</td>
                    <td>{racer.status || 'OK'}</td>
                    <td>{racer.bib}</td>
                    <td>
                      {racer.firstName}{' '}
                      {racer.lastName}
                    </td>
                    <td>{racer.country}</td>
                    <td>{racer.yob}</td>
                    <td>{racer.club}</td>
                    {/* For FIS tab: show FIS ID */}
                    {isFIS && <td>{racer.fisCode}</td>}
                    {/* For USSA tab: show USSA ID, and FIS ID if data present */}
                    {!isFIS && <td>{racer.ussaCode}</td>}
                    {!isFIS && (isCombinedRace || hasFisCodeData) && <td>{racer.fisCode}</td>}
                    {/* For FIS tab: show FIS Pts */}
                    {isFIS && <td>{racer.fisPoints}</td>}
                    {/* For USSA tab: show NRL Pts, and FIS Pts if data present */}
                    {!isFIS && <td>{racer.ussaPoints}</td>}
                    {!isFIS && (isCombinedRace || hasFisPointsData) && <td>{racer.fisPoints}</td>}
                    <td>{racer.time}</td>
                    {/* Sprint heats: show Highest Level column */}
                    {hasSprintHeatsData && <td>{racer.highestLevel}</td>}
                    {isPointsComplete && <td>{racer.racePoints}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-info">
              {racers.length} racers
            </div>
          </div>
        ) : (
          <div className="no-data">
            No racer data loaded. Upload a CSV file first.
          </div>
        )}
      </div>
    </div>
  );
}

export default DataWorkflow;
